import { Button, ConfirmDialog, SelectionActionBar } from '@/components/common'
import PrepackDetailDrawer from './PrepackDetailDrawer'
import PrepackPublishDialog from './PrepackPublishDialog'
import styles from './PackListOverlays.module.css'

/**
 * What a pack list opens over itself (lifted from the Library for #1080):
 * the selection bar (Publish to surfaces, Delete selected), the pack's
 * drawer, the delete confirmation and the publish dialog. `list` is
 * `usePackList`'s answer. `onEdit(pack)` opens the pack in Create;
 * `onEditSchedule` and `scheduleSource` make the drawer's Edit a split
 * button (#863). `regionRef` is the list's region, where focus goes when a
 * delete or the drawer took away the control that had it (#1054).
 */
export default function PackListOverlays({ list, canConfigure, canPublish, templates, scheduleSource = null, onEdit, onEditSchedule, regionRef }) {
  const { openPack, selectedPacks, selectedPackIds, selectedBlocked, lifecycle, packDelete } = list
  const { requestDelete, preparingDelete } = packDelete
  return (
    <>
      {selectedPacks.size > 0 ? (
        <SelectionActionBar
          count={selectedPacks.size}
          noun="prepack"
          note={canConfigure && selectedBlocked > 0 ? 'Deleting published prepacks requires publish permission.' : undefined}
          onClear={() => list.setSelectedPacks(new Map())}
          sticky
          className={styles.selectionBar}
        >
          <Button
            variant="primary"
            size="compact"
            onClick={() => list.setPublishDialogOpen(true)}
            disabled={!canConfigure && !canPublish}
          >
            Publish to surfaces
          </Button>
          {canConfigure ? (
            <Button
              variant="danger"
              size="compact"
              onClick={() => requestDelete(selectedPackIds, { fromSelection: true })}
              loading={preparingDelete}
              disabled={selectedBlocked === selectedPacks.size}
            >
              Delete selected
            </Button>
          ) : null}
        </SelectionActionBar>
      ) : null}

      {openPack ? (
        <PrepackDetailDrawer
          slug={list.slug}
          pack={openPack}
          cardPack={list.drawerPack}
          templates={templates}
          access={{ canConfigure, canPublish, canDelete: list.canDelete(openPack) }}
          scheduleSource={scheduleSource}
          onEdit={() => onEdit(openPack)}
          onEditSchedule={onEditSchedule}
          onReset={list.openSaved}
          actions={{
            analyze: lifecycle.analyze,
            regenerate: lifecycle.regenerate,
            changeLifecycle: lifecycle.changeLifecycle,
            requestDelete: (pack) => requestDelete([pack.id]),
            publishToSurfaces: list.publishOne,
          }}
          pending={{ ...lifecycle.pending, deleting: preparingDelete }}
          onClose={() => list.setOpenPack(null)}
          returnFocusFallbackRef={regionRef}
        />
      ) : null}

      <ConfirmDialog {...packDelete.confirmProps} returnFocusFallbackRef={regionRef} />
      {/* Must stay mounted across close; never change this to {open && <PrepackPublishDialog />}, because dismissed batches continue in the background. */}
      <PrepackPublishDialog
        open={list.publishDialogOpen}
        onClose={() => list.setPublishDialogOpen(false)}
        initialPackIds={selectedPackIds}
        mode="publish"
        onSuccess={(completedIds) => {
          const completed = new Set(completedIds || [])
          list.setSelectedPacks((current) => new Map([...current].filter(([id]) => !completed.has(id))))
        }}
      />
    </>
  )
}
